import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// PO 2026-02 · Remote Customer Visual Intake V1A — mobile capture page.
// No auth. No app install. No JobClose customer portal. Deterministic
// prompts + camera-capture photo uploads + optional per-photo typed
// comment. Feature-flagged server-side; if the flag is OFF the /public
// endpoints 404 and this page shows a "link is no longer valid" message.

const API = import.meta.env?.VITE_API_ROOT
  || (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL)
  || "";

export default function VisualIntake() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);  // {name, ok, error?}
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/public/visual-intake/${token}`);
      if (!r.ok) { setError("This link is no longer valid or has expired."); return; }
      const j = await r.json();
      setMeta(j);
      setError(null);
    } catch (e) {
      setError("Could not reach JobClose. Please try again.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      if (comment && comment.trim()) fd.append("comment", comment.trim());
      try {
        const r = await fetch(`${API}/api/public/visual-intake/${token}/upload`, {
          method: "POST", body: fd,
        });
        const j = await r.json().catch(() => ({}));
        setUploads((prev) => [...prev, {
          name: f.name,
          ok: r.ok,
          error: r.ok ? null : (j.detail || `Upload failed (${r.status})`),
        }]);
      } catch (err) {
        setUploads((prev) => [...prev, {
          name: f.name, ok: false, error: "Network error.",
        }]);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    setComment("");
    await load();
  };

  const onSubmit = async () => {
    try {
      const r = await fetch(
        `${API}/api/public/visual-intake/${token}/submit`,
        { method: "POST" });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        alert(j.detail || "Please upload at least one photo before submitting.");
        return;
      }
      setSubmitted(true);
    } catch { alert("Network error. Please try again."); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="visual-intake-loading">
      <div className="text-slate-500">Loading…</div>
    </div>);
  if (error || !meta) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="visual-intake-invalid">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
        <div className="text-lg font-semibold text-slate-900 mb-2">Link unavailable</div>
        <div className="text-sm text-slate-600">
          {error || "This link is no longer valid or has expired."}
        </div>
      </div>
    </div>);

  if (submitted) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="visual-intake-submitted">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
        <div className="text-lg font-semibold text-slate-900 mb-2">
          {meta.language === "es" ? "¡Gracias!" : "Thank you!"}
        </div>
        <div className="text-sm text-slate-600">
          {meta.language === "es"
            ? `Recibimos ${meta.photos_received} foto${meta.photos_received === 1 ? "" : "s"}. ${meta.contractor_display_name} las revisará pronto.`
            : `We received ${meta.photos_received} photo${meta.photos_received === 1 ? "" : "s"}. ${meta.contractor_display_name} will review them shortly.`}
        </div>
      </div>
    </div>);

  const remaining = Math.max(0, (meta.max_photos || 12) - (meta.photos_received || 0));
  const t = (en, es) => (meta.language === "es" ? es : en);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="visual-intake-page">
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-xl mx-auto">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            {t("From", "De parte de")}
          </div>
          <div className="text-lg font-semibold text-slate-900" data-testid="visual-intake-company">
            {meta.company_name}
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-white rounded-lg shadow-sm p-5">
          <div className="text-base font-semibold text-slate-900 mb-1">
            {t(`Hi ${meta.customer_first_name}, we need a few photos.`,
                `Hola ${meta.customer_first_name}, necesitamos algunas fotos.`)}
          </div>
          <div className="text-sm text-slate-600">
            {t(`This helps ${meta.contractor_display_name} understand what happened so they can decide the next step.`,
                `Esto ayuda a ${meta.contractor_display_name} a entender lo que pasó para decidir el siguiente paso.`)}
          </div>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-5">
          <div className="text-sm font-semibold text-slate-900 mb-2">
            {meta.title}
          </div>
          <ol className="list-decimal ml-5 text-sm text-slate-700 space-y-1" data-testid="visual-intake-prompts">
            {(meta.prompts || []).map((p, i) => (<li key={`p-${i}`}>{p}</li>))}
            {(meta.custom_prompts || []).map((p, i) => (
              <li key={`cp-${i}`} data-testid={`visual-intake-custom-prompt-${i}`}>{p}</li>
            ))}
          </ol>
        </section>

        <section className="bg-white rounded-lg shadow-sm p-5 space-y-3">
          <label className="block text-sm font-medium text-slate-700">
            {t("Optional note (what happened, when it started)",
                "Nota opcional (qué pasó, cuándo empezó)")}
          </label>
          <textarea
            data-testid="visual-intake-comment"
            className="w-full border border-slate-300 rounded-md p-2 text-sm"
            rows={3} maxLength={2000}
            value={comment}
            onChange={(e) => setComment(e.target.value)} />

          <input
            ref={inputRef}
            data-testid="visual-intake-file-input"
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            multiple
            capture="environment"
            className="block w-full text-sm text-slate-700"
            onChange={onFile}
            disabled={uploading || remaining <= 0} />
          <div className="text-xs text-slate-500">
            {t(`Up to ${remaining} more photo${remaining === 1 ? "" : "s"}. Max 10 MB each.`,
                `Hasta ${remaining} foto${remaining === 1 ? "" : "s"} más. Máximo 10 MB cada una.`)}
          </div>

          {uploads.length > 0 && (
            <ul className="text-xs space-y-1 pt-2" data-testid="visual-intake-uploads-list">
              {uploads.map((u, i) => (
                <li key={i} className={u.ok ? "text-emerald-700" : "text-red-700"}>
                  {u.ok ? "✓" : "✗"} {u.name}
                  {u.error ? ` — ${u.error}` : ""}
                </li>
              ))}
            </ul>
          )}
        </section>

        <button
          data-testid="visual-intake-submit"
          disabled={uploading || (meta.photos_received || 0) === 0}
          className="w-full py-3 rounded-md bg-slate-900 text-white text-sm font-semibold disabled:opacity-50"
          onClick={onSubmit}>
          {t(`Send to ${meta.contractor_display_name}`,
              `Enviar a ${meta.contractor_display_name}`)}
        </button>

        <div className="text-xs text-slate-400 text-center pb-6">
          {t("Please stay safe. Do not climb or enter unsafe areas for these photos.",
              "Por favor, cuida tu seguridad. No subas ni entres a áreas inseguras para estas fotos.")}
        </div>
      </main>
    </div>
  );
}
